import { useEffect, useState } from 'react';
import { Upload } from '@b-design/fusion';
import { propsFilter } from '@/utils';
import $i18n from '../../../i18n';

interface PdfUploaderProps {
  // 图片上传地址
  action?: string;
  // 支持的文件类型
  accept?: string[];
  // 上传文件大小限制，单位 byte
  maxSize?: number;
  // 宽高限制
  fileWidAndHei?: (string | number)[];
  // 子元素（自定义触发内容）
  children?: React.ReactNode;
  // 上传所需的额外参数
  data?: object;
  // 文件主体参数名
  name?: string;
  // 文件列表
  value?: any[];
  // 请求的返回值格式化，默认按 oss 上传接口处理，在组件 file 的 response 中可以拿到格式化后的接口数据
  formatter?: Function;
  // 这是value 转化方法，value 转化为组件展示所需的结构，主要是为了保持出入参一致，底层 upload 的适配就交给它了
  transformer?: Function;
  // 上传前的逻辑处理
  beforeUpload?: Function;
  // 上传成功回调
  onSuccess?: Function;
  // 上传前校验报错
  onCheckFail?: (err, response, e) => void;
  // 上传报错（含限制校验和服务端错误）回调
  onError?: (err, response, e) => void;
  // 移除上传文件回调
  onRemove?: Function;
  // 文件变化回调
  onChange?: Function;
  // 禁用
  disabled?: boolean | null;
  //
  itemRender?: Function;
}

/**
 * 上传返回值格式化
 * originData 将作为 value 保存下来并提供出去
 *
 * @param {object} res 服务器端的返回值
 * @returns {object} 格式化后的文件对象
 */
export function defaultFormatter(rst) {
  const res = rst || {};
  const data = res.data || {};
  return {
    // Component Needed
    code: res.success,
    publicUrl: data.publicUrl || data.fileUrl || data.url,
    relativePath: data.relativePath || data.fileUrl || data.url,
    // Submit Needed
    success: res.success,
    errCode: res.errorCode, // TYPEERR | SIZEERR
    originData: data,
  };
}

/**
 * value 转化为组件展示所需的结构
 *
 * @param {object|array} data 服务端带出的已上传文件信息
 * @returns 符合接口标准兼容性良好的文件列表数据
 */
export function defaultTransformer(data) {
  let files;
  try {
    files = Array.isArray(data) ? data : Object.values(data);
  } catch (e) {
    files = [];
  }
  return files.map((file) => ({
    // 主要信息
    name: file.fileName || file.name,
    fileName: file.fileName || file.name,
    status: 'done',
    size: file.fileSize,
    url: file.publicUrl || file.fileUrl || file.url,
    publicUrl: file.publicUrl || file.fileUrl || file.url,
    fileURL: file.publicUrl || file.fileUrl || file.url,
    imgURL: file.publicUrl || file.fileUrl || file.url,
    // 额外信息
    mimeType: file.mimeType,
    ossKey: file.ossKey,
    suffix: file.suffix,
  }));
}

const PdfUploader = (props: PdfUploaderProps) => {
  const {
    action = '',
    accept = undefined,
    maxSize = undefined,
    fileWidAndHei = [],
    data = {},
    value = [],
    formatter = defaultFormatter,
    transformer = defaultTransformer,
    beforeUpload = () => true,
    onSuccess = () => {},
    onCheckFail = () => {},
    onError = () => {},
    onRemove = () => {},
    onChange = () => {},
    disabled = false,
    itemRender = () => {},
  } = props;

  const [fileValue, setFileValue] = useState(value);

  useEffect(() => {
    onChange(fileValue);
  }, [fileValue]);

  const _onSuccess = (formattedData, file) => {
    const _value = {
      ...(formattedData.originData ||
        (formattedData.response && formattedData.response.originData) ||
        {}),
    };
    if (fileValue.length === 0) {
      _value.order = 0;
    } else {
      _value.order = fileValue[fileValue.length - 1].order + 1;
    }

    const _newValue = [_value];
    setFileValue(_newValue);
    onSuccess(value, formattedData, file);
  };

  const _onError = (e) => {
    let err: any = {};
    if (e.error.code === 'BEFOREUPLOAD_REJECT' && e.error.message === 'BEFOREUPLOAD_REJECT') {
      return false;
    } else {
      err = new Error(
        $i18n.get({ id: 'aopf.Uploader.component.image.SystemException', dm: '系统异常' }),
      );

      err.fileName = e.name;
      err.fileSize = e.size;
      err.fileType = e.type;
      err.code = 'SYSERR';
      err.unknown = true;
      return onError(err, e.response, e);
    }
  };

  const _onRemove = (file) => {
    const _value = fileValue.filter(
      (v) =>
        !(
          v.downloadURL === file.downloadURL ||
          v.fileUrl === file.downloadURL ||
          v.url === file.url
        ),
    );

    setFileValue(_value);
    onRemove(file);
    console.log('onRemove');
  };

  const newProps = propsFilter(props, ['onChange']);

  const accepts = Array.isArray(accept) ? accept.join(',') : accept;
  const extraData = Object.assign({}, data);
  const _onPreview = () => {
    console.log('onPreview');
    let a = document.getElementById('reupload');
    a?.click();
  };

  return (
    <>
      <Upload.Card
        {...newProps.other}
        disabled={disabled}
        action={action}
        reUpload={!disabled}
        showDownload={false}
        listType={'card'}
        accept={accepts}
        data={extraData}
        value={transformer(fileValue)}
        formatter={newProps.formatter || formatter}
        onSuccess={_onSuccess}
        onError={_onError}
        onRemove={_onRemove}
        limit={1}
        itemRender={itemRender}
        onPreview={_onPreview}
      >
        <span>
          {$i18n.get({ id: 'aopf.Uploader.component.pdf.ChineseUpload', dm: 'Upload' })}
        </span>
      </Upload.Card>
    </>
  );
};

export default PdfUploader;
